// src/catering/rations/RationsListe.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Card, Table, Row, Col, Form, Button, Spinner, Pagination, Badge, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate, formatCurrency } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn"><i className="ri-close-line fs-5"></i></button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   PAGINATION
   ============================================================ */
const TablePagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <Pagination className="pagination-charte mb-0">
            <Pagination.Prev onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </Pagination.Item>
                );
            })}
            <Pagination.Next onClick={() => onPageChange(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function RationsListe() {
    const navigate = useNavigate();
    const [distributions, setDistributions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [filterText, setFilterText] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('date_distribution');
    const [sortDirection, setSortDirection] = useState('desc');
    const [updatingId, setUpdatingId] = useState(null);

    // Toast
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchDistributions(); fetchStats(); }, [filtreStatut]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchDistributions = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            const url = `${API_BASE_URL}/rations.php${params.toString() ? '?' + params.toString() : ''}`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
            const result = await res.json();
            setDistributions(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/rations.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setStats(result.data);
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const updateStatut = async (id, statut) => {
        setUpdatingId(id);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/rations.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_statut', id_distribution: id, statut })
            });
            const result = await res.json();
            if (result.success) {
                showToast('success', 'Statut mis à jour', `La distribution est maintenant "${statut}".`);
                await fetchDistributions();
                await fetchStats();
            } else {
                showToast('error', 'Erreur', result.message || 'Impossible de mettre à jour');
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setUpdatingId(null);
        }
    };

    const resetFilters = () => {
        setFilterText('');
        setFiltreStatut('tous');
        setDateDebut('');
        setDateFin('');
        setCurrentPage(1);
    };

    /* ============================================================
       FILTRAGE / TRI / PAGINATION
       ============================================================ */
    const filteredAndSorted = useMemo(() => {
        let filtered = distributions.filter(d => {
            const textMatch =
                (d.type_libelle || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (d.nom_utilisateur || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (d.prenom_utilisateur || '').toLowerCase().includes(filterText.toLowerCase());

            let dateMatch = true;
            const dDate = d.date_distribution?.split('T')[0] || '';
            if (dateDebut && dateFin) dateMatch = dDate >= dateDebut && dDate <= dateFin;
            else if (dateDebut) dateMatch = dDate >= dateDebut;
            else if (dateFin) dateMatch = dDate <= dateFin;

            return textMatch && dateMatch;
        });

        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (sortField === 'date_distribution' || sortField === 'created_at') {
                aVal = new Date(aVal || 0);
                bVal = new Date(bVal || 0);
            }
            return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });

        return filtered;
    }, [distributions, filterText, dateDebut, dateFin, sortField, sortDirection]);

    const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    /* ============================================================
       STATISTIQUES DYNAMIQUES
       ============================================================ */
    const dynamicStats = useMemo(() => {
        const total = filteredAndSorted.length;
        const servies = filteredAndSorted.filter(d => d.statut === 'servie').length;
        const planifiees = filteredAndSorted.filter(d => d.statut === 'planifiee').length;
        const annulees = filteredAndSorted.filter(d => d.statut === 'annulee').length;
        return { total, servies, planifiees, annulees };
    }, [filteredAndSorted]);

    /* ============================================================
       HELPERS
       ============================================================ */
    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortField === field && (
                    <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const getStatutConfig = (statut) => {
        const map = {
            planifiee: { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Planifiée', icon: 'ri-time-line' },
            servie:    { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Servie',    icon: 'ri-check-line' },
            annulee:   { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Annulée',   icon: 'ri-close-line' }
        };
        return map[statut] || map.planifiee;
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !distributions.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des distributions...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-restaurant-line"></i></div>
                            <div>
                                <h5 className="header-title">Gestion des rations</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {dynamicStats.total}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-check-line"></i> Servies {dynamicStats.servies}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-time-line"></i> Planifiées {dynamicStats.planifiees}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-close-line"></i> Annulées {dynamicStats.annulees}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchDistributions} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/catering/rationsTypes')}>
                                <i className="ri-list-check"></i> Types
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/catering/rationsAjouter')}>
                                <i className="ri-add-line"></i> Nouvelle distribution
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Aujourd'hui</span>
                                        <h3 className="kpi-value-charte">{stats.aujourdhui || 0}</h3>
                                        <span className="kpi-unit-charte">{stats.servies_aujourdhui || 0} servie(s)</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-calendar-2-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Ce mois</span>
                                        <h3 className="kpi-value-charte">{stats.mois || 0}</h3>
                                        <span className="kpi-unit-charte">distributions</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-home-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Internes</span>
                                        <h3 className="kpi-value-charte">{stats.internes_mois || 0}</h3>
                                        <span className="kpi-unit-charte">ce mois</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-building-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Externes</span>
                                        <h3 className="kpi-value-charte">{stats.externes_mois || 0}</h3>
                                        <span className="kpi-unit-charte">ce mois</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher type, bénéficiaire..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="2">
                                    <Form.Control type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="filter-select-charte" />
                                </Col>
                                <Col md="2">
                                    <Form.Control type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="filter-select-charte" />
                                </Col>
                                <Col md="2">
                                    <Form.Select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} className="filter-select-charte">
                                        <option value="tous">Tous statuts</option>
                                        <option value="planifiee">Planifiées</option>
                                        <option value="servie">Servies</option>
                                        <option value="annulee">Annulées</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1">
                                    <Form.Select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="filter-select-charte">
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1" className="text-end">
                                    {(filterText || dateDebut || dateFin || filtreStatut !== 'tous') && (
                                        <Button onClick={resetFilters} className="btn-modern-light-outline">
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-restaurant-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Distributions</span>
                                    <span className="badge-header-light">{filteredAndSorted.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <SortableHeader field="date_distribution">Date</SortableHeader>
                                            <SortableHeader field="nom_utilisateur">Bénéficiaire</SortableHeader>
                                            <SortableHeader field="type_libelle">Type de ration</SortableHeader>
                                            <th className="text-center">Fourniture</th>
                                            <th className="text-center">Qté</th>
                                            <th className="text-end">Valeur</th>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map(d => {
                                            const statutCfg = getStatutConfig(d.statut);
                                            const isUpdating = updatingId === d.id_distribution;
                                            return (
                                                <tr key={d.id_distribution}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="date-icon-charte">
                                                                <i className="ri-calendar-line"></i>
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {formatDate(d.date_distribution)}
                                                                </div>
                                                                {d.heure_distribution && (
                                                                    <small className="text-muted">
                                                                        <i className="ri-time-line me-1"></i>
                                                                        {d.heure_distribution}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ minWidth: '180px' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="beneficiaire-avatar-charte">
                                                                {(d.prenom_utilisateur || '?').charAt(0)}{(d.nom_utilisateur || '?').charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {d.prenom_utilisateur} {d.nom_utilisateur}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-semibold">{d.type_libelle}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        {d.type_fourniture === 'interne' ? (
                                                            <span className="badge-modern">
                                                                <i className="ri-home-line"></i> Interne
                                                            </span>
                                                        ) : (
                                                            <span className="badge-modern" style={{ background: '#fef3c7', color: '#92400e' }}>
                                                                <i className="ri-building-line"></i> Externe
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="quantity-badge-charte">{d.quantite}</span>
                                                    </td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>
                                                        {formatCurrency((d.quantite || 1) * (d.valeur_unitaire || 0))}
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="status-badge-charte" style={{ background: statutCfg.gradient }}>
                                                            <i className={statutCfg.icon}></i> {statutCfg.label}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        {d.statut === 'planifiee' && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Marquer comme servie</Tooltip>}>
                                                                <Button
                                                                    size="sm"
                                                                    className="btn-modern-primary"
                                                                    onClick={() => updateStatut(d.id_distribution, 'servie')}
                                                                    disabled={isUpdating}
                                                                >
                                                                    {isUpdating ? (
                                                                        <Spinner size="sm" animation="border" style={{ width: '14px', height: '14px' }} />
                                                                    ) : (
                                                                        <><i className="ri-check-line"></i> Servir</>
                                                                    )}
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                        {d.statut === 'servie' && (
                                                            <span className="text-muted small">
                                                                <i className="ri-checkbox-circle-fill me-1" style={{ color: COLORS.success }}></i>
                                                                Servie
                                                            </span>
                                                        )}
                                                        {d.statut === 'annulee' && (
                                                            <span className="text-muted small">
                                                                <i className="ri-close-circle-fill me-1" style={{ color: COLORS.danger }}></i>
                                                                Annulée
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="8">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-restaurant-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucune distribution trouvée</h6>
                                                        <p className="text-muted small mb-3">
                                                            {filterText || dateDebut || dateFin || filtreStatut !== 'tous'
                                                                ? "Essayez de modifier vos critères de recherche."
                                                                : "Commencez par créer votre première distribution."}
                                                        </p>
                                                        {!(filterText || dateDebut || dateFin || filtreStatut !== 'tous') && (
                                                            <Button className="btn-modern-primary" onClick={() => navigate('/catering/rationsAjouter')}>
                                                                <i className="ri-add-line me-2"></i>Ajouter
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSorted.length)}</strong> sur <strong>{filteredAndSorted.length}</strong> distribution(s)
                            </div>
                            <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--secondary); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte { background: white; border-radius: 12px; padding: 0.6rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid; transition: all 0.25s ease; height: 100%; }
                .kpi-charte:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value-charte { font-size: 1.35rem; font-weight: 700; margin: 0.3rem 0; color: #1e293b; }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte { background: linear-gradient(135deg, var(--primary-dark), var(--primary)); border-radius: 12px; border: none; box-shadow: 0 8px 24px rgba(41,90,167,0.15); }
                .search-box-charte { position: relative; }
                .search-icon-charte { position: absolute; top: 50%; left: 1rem; transform: translateY(-50%); color: var(--primary); font-size: 1rem; z-index: 2; pointer-events: none; }
                .search-input-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem 0.6rem 2.75rem !important; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem !important; font-size: 0.85rem; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); color: white; border-radius: 50px; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 500; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-light-outline:hover { background: rgba(255,255,255,0.25); color: white; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; border: none; }
                .badge-header-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 50px; padding: 0.25rem 0.75rem; font-size: 0.75rem; font-weight: 600; }

                /* ===== TABLE ===== */
                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }

                .date-icon-charte { width: 38px; height: 38px; border-radius: 10px; background: ${COLORS.primary}15; color: var(--primary); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
                .beneficiaire-avatar-charte { width: 38px; height: 38px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--primary-dark)); color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem; flex-shrink: 0; box-shadow: 0 2px 6px rgba(41,90,167,0.2); }
                .quantity-badge-charte { display: inline-flex; align-items: center; justify-content: center; min-width: 40px; padding: 0.25rem 0.5rem; background: #f1f5f9; border-radius: 8px; font-weight: 700; font-size: 0.8rem; color: #1e293b; }
                .status-badge-charte { display: inline-flex; align-items: center; gap: 4px; color: white; padding: 0.3rem 0.75rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); flex-wrap: wrap; gap: 1rem; }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link { border-radius: 10px; border: 1.5px solid #e2e8f0; color: #1e293b; padding: 0.5rem 0.9rem; font-weight: 500; font-size: 0.85rem; transition: all 0.2s; }
                .pagination-charte .page-item.active .page-link { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border-color: var(--primary); color: white; box-shadow: 0 4px 12px rgba(41,90,167,0.3); }
                .pagination-charte .page-item .page-link:hover { background: #f1f5f9; border-color: var(--secondary); color: var(--primary); transform: translateY(-1px); }

                /* ===== BOUTONS ===== */
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(33,172,205,0.15); }
                .btn-modern-primary { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(41,90,167,0.3); display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white; }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; opacity: 0.8; }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}